/* eslint-disable multiline-comment-style */
import type { WebContainer, WebContainerProcess } from '@webcontainer/api';
import type { ITerminal } from '~/types/terminal';
import { withResolvers } from './promises';
import { atom } from 'nanostores';
import { ErrorPatterns } from './terminal/ErrorPatterns';
import { ErrorBuffer } from './terminal/ErrorBuffer';
import { TerminalErrorEmitter } from './terminal/TerminalErrorEmitter';

export enum TerminalStatus {
  DISCONNECTED = 'disconnected',
  CONNECTED = 'connected',
  EXECUTING = 'executing',
  FREE = 'free',
}

export async function newShellProcess(webcontainer: WebContainer, terminal: ITerminal) {
  console.log('Starting new shell process with terminal dimensions:', { cols: terminal.cols, rows: terminal.rows });

  const args: string[] = [];

  // we spawn a JSH process with a fallback cols and rows in case the process is not attached yet to a visible terminal
  const process = await webcontainer.spawn('/bin/jsh', ['--osc', ...args], {
    terminal: {
      cols: terminal.cols ?? 80,
      rows: terminal.rows ?? 15,
    },
  });
  console.log('JSH process spawned successfully');

  const input = process.input.getWriter();
  const output = process.output;

  const jshReady = withResolvers<void>();

  let isInteractive = false;
  output.pipeTo(
    new WritableStream({
      write(data) {
        console.log('Received output data:', data);

        if (!isInteractive) {
          const [, osc] = data.match(/\x1b\]654;([^\x07]+)\x07/) || [];

          if (osc === 'interactive') {
            console.log('Shell entered interactive mode');
            isInteractive = true;
            jshReady.resolve();
          }
        }

        terminal.write(data);
      },
    }),
  );

  terminal.onData((data) => {
    console.log('Terminal input received:', data);

    if (isInteractive) {
      console.log('Writing input to shell:', data);
      input.write(data);
    }
  });

  await jshReady.promise;
  console.log('Shell process ready');

  return process;
}

export type ExecutionResult = { output: string; exitCode: number } | undefined;

export class DualiteShell {
  #initialized: (() => void) | undefined;
  #readyPromise: Promise<void>;
  #webcontainer: WebContainer | undefined;
  #terminal: ITerminal | undefined;
  #process: WebContainerProcess | undefined;
  executionState = atom<
    { sessionId: string; active: boolean; executionPrms?: Promise<any>; abort?: () => void } | undefined
  >();
  #outputStream: ReadableStreamDefaultReader<string> | undefined;
  #shellInputStream: WritableStreamDefaultWriter<string> | undefined;
  #lastCommandContext = '';
  #errorCleanupTimer: NodeJS.Timeout | null = null;
  #currentInputBuffer = '';

  isInitialized = atom(false);
  isRunning = atom<{ executing: boolean; executingCommand?: string }>({ executing: false });

  // New utility classes with proper connection
  #errorBuffer = new ErrorBuffer();
  #errorEmitter: TerminalErrorEmitter;

  constructor() {
    console.log('Initializing DualiteShell');
    this.#readyPromise = new Promise((resolve) => {
      this.#initialized = resolve;
    });

    // Initialize error handling system with connected components
    this.#errorBuffer = new ErrorBuffer();
    this.#errorEmitter = new TerminalErrorEmitter(this.#errorBuffer);

    this.startErrorCleanupTimer();
  }

  ready() {
    return this.#readyPromise;
  }

  async init(webcontainer: WebContainer, terminal: ITerminal) {
    console.log('Initializing shell with webcontainer and terminal');
    this.#webcontainer = webcontainer;
    this.#terminal = terminal;

    const { process, output } = await this.newDualiteShellProcess(webcontainer, terminal);
    console.log('Dualite shell process created');

    this.#process = process;
    this.#outputStream = output.getReader();
    await this.waitTillOscCode('interactive');
    this.isInitialized.set(true);
    console.log('Shell initialization complete');
    this.#initialized?.();
  }

  async initNonInteractive(webcontainer: WebContainer, terminal: ITerminal) {
    console.log('Initializing non-interactive shell with webcontainer and terminal');
    this.#webcontainer = webcontainer;
    this.#terminal = terminal;
    this.isInitialized.set(true);
    this.#initialized?.();
  }

  get terminal() {
    return this.#terminal;
  }

  get process() {
    return this.#process;
  }

  get status() {
    return this.isRunning;
  }

  get currentExecutingCommand() {
    return this.isRunning.get().executingCommand;
  }

  kill() {
    if (this.#process) {
      this.#process.kill();
    }
  }

  async executeCommand(
    sessionId: string,
    command: string,
    abort?: () => void,
    isPipedCommand: boolean = false,
  ): Promise<ExecutionResult> {
    console.log('Executing command:', { sessionId, command, isPipedCommand });

    if (!this.process || !this.terminal) {
      console.log('No process or terminal available');
      return undefined;
    }

    this.#lastCommandContext = command.trim();
    this.#errorBuffer.clear();

    const state = this.executionState.get();

    if (state?.active && state.abort) {
      console.log('Aborting previous command execution');
      state.abort();

      // this.isRunning.set({ executing: false, executingCommand: undefined });
    }

    /*
     * (old comment, not mine)
     * interrupt the current execution
     *  this.#shellInputStream?.write('\x03');
     */

    this.terminal.input('\x03');
    await this.waitTillOscCode('prompt');

    /*
     * @mukul @shivam @sayan @vaibhavi
     *
     * about the ^cyarn run dev thing
     * okay so this "\x03" is the interrupt character, or "Ctrl+C"
     * This is added to kill the current process
     * then we need to wait for the prompt to appear again, for that we use the waitTillOscCode('prompt')
     * but since the last character we added was "\x03", it stays there, gets prefixed to the next shell prompt!!
     * not sure exactly why this happens, but it does.
     * solution?
     * Added a new "Enter" character to the end of the command, which is the same as pressing enter in the shell,
     * so boom💥 it works now - needs more testing though.
     */

    // this.terminal.input('\n'); // Ensure the shell processes the interrupt and issues a fresh prompt

    if (state && state.executionPrms) {
      console.log('Waiting for previous execution to complete', state);
      this.isRunning.set({ executing: true });
      await state.executionPrms;
    }

    console.log('Starting new command execution', state, command.trim());
    this.isRunning.set({ executing: true, executingCommand: command.trim() }); // we can set the running command from here. (only ones coming from AI, not manual)
    this.terminal.input(command.trim() + ' \n');

    const executionPromise = isPipedCommand ? this.getPipedCommandExecutionResult() : this.getCurrentExecutionResult();

    this.executionState.set({ sessionId, active: true, executionPrms: executionPromise, abort });

    const resp = await executionPromise;
    console.log('Command execution completed:', resp);

    this.executionState.set({ sessionId, active: false });

    if (resp) {
      try {
        resp.output = cleanTerminalOutput(resp.output);
        console.log('Cleaned command output:', resp.output);

        this.#errorBuffer.append(resp.output);

        if (this.#errorEmitter.getErrorState() && resp.exitCode === 0 && !ErrorPatterns.hasErrorPatterns(resp.output)) {
          console.log('Previous error state resolved');
          this.#errorEmitter.setErrorState(false);
          this.#errorEmitter.emitErrorResolution(this.#lastCommandContext);
        }

        this.detectTerminalErrors(resp.output, resp.exitCode, this.#lastCommandContext);
      } catch (error) {
        console.log('failed to format terminal output', error);
      }
    }

    console.log('Setting isRunning to false', this.isRunning.get());
    this.isRunning.set({ executing: false, executingCommand: undefined });

    return resp;
  }

  async runShellActionFromPreview(command: string) {
    console.log('Running shell action from preview', command);

    if (!this.process || !this.terminal) {
      console.log('No process or terminal available');
      return undefined;
    }

    this.terminal.input(command.trim() + ' \n');
    this.isRunning.set({ executing: true, executingCommand: command.trim() });

    const resp = await this.getCurrentExecutionResult();
    console.log('Shell action from preview completed', resp);

    // so the problem is with "&&", this sends 'done' status on yarn install and not waits for yarn run dev
    // so we need to wait for 5 seconds before setting isRunning to false
    this.isRunning.set({ executing: false, executingCommand: undefined });
    return resp;
  }

  /**
   * Runs the "yarn run lint:dualite" and "yarn run tsc:dualite" commands and returns the output.
   * @returns The full output of the linting and typescript compilation commands.
   */
  async runLintAndTypeCheck(): Promise<string | undefined> {
    console.log('Executing "yarn run lint:dualite" and "yarn run tsc:dualite" commands...');

    if (!this.#webcontainer || !this.#terminal) {
      console.log('No webcontainer or terminal available');
      return undefined;
    }

    let combinedOutput = '';

    // Helper function to run a command and capture its output
    const runCommand = async (command: string, args: string[]): Promise<{ output: string; exitCode: number }> => {
      this.isRunning.set({ executing: true, executingCommand: `${command} ${args.join(' ')}` });

      const process = await this.#webcontainer!.spawn(command, args);
      let output = '';

      process.output.pipeTo(
        new WritableStream({
          write: (data) => {
            output += data;
            this.#terminal?.write(data);
          },
        }),
      );

      const exitCode = await process.exit;
      this.isRunning.set({ executing: false, executingCommand: undefined });
      return { output, exitCode };
    };

    // Run lint command
    const lintResult = await runCommand('yarn', ['run', 'lint:dualite']);
    combinedOutput += lintResult.output;
    console.log('Lint command finished with exit code:', lintResult.exitCode);

    // if (lintResult.exitCode !== 0) {
    //   console.error('Lint command failed. Aborting tsc command.');
    //   return combinedOutput;
    // }

    // Run tsc command
    const tscResult = await runCommand('yarn', ['run', 'tsc:dualite']);
    combinedOutput += `\n${tscResult.output}`;
    console.log('TSC command finished with exit code:', tscResult.exitCode);

    return combinedOutput;
  }

  /**
   * Detects errors in terminal output
   * @param output The cleaned terminal output
   * @param exitCode The command exit code
   * @param command The command that was executed
   */
  detectTerminalErrors(output: string, exitCode: number, command: string): void {
    console.log('Detecting errors in output:', { output, command, exitCode });

    try {
      const commandChunks = output.split(/\n*~\/[^\n]*\n❯/);
      const latestChunk = commandChunks[commandChunks.length - 1];

      if (!latestChunk || !latestChunk.trim()) {
        console.log('No output to analyze');
        return;
      }

      // Check for shell prompt in the output
      if (this.#errorEmitter.processShellPrompt(output)) {
        console.log('Shell prompt detected in output, resetting error state');
        return;
      }

      // Clean the output before processing
      const cleanedOutput = this.#errorBuffer.cleanTerminalForErrors(latestChunk);
      const errorMessage = this.#errorBuffer.extractErrorMessage(latestChunk);

      // Enhanced error pattern detection using the ErrorPatterns class
      const hasError = ErrorPatterns.hasErrorPatterns(cleanedOutput);
      const exitCodeError = exitCode !== 0;

      if (hasError || exitCodeError) {
        console.log('Error detected:', { hasError, exitCodeError });

        if (ErrorPatterns.isBenignError(cleanedOutput)) {
          console.log('Benign error detected, ignoring');
          return;
        }

        // Emit the error with cleaned output
        this.#errorEmitter.emitTerminalError(cleanedOutput, exitCode, command);
      }
    } catch (error) {
      console.error('Error in terminal error detection:', error);
    }
  }

  /**
   * Resets the error state and clears processed errors
   * Called when user manually fixes errors
   */
  resetErrorState(): void {
    console.log('Resetting error state');
    this.#errorEmitter.resetErrorState();
  }

  /**
   * Starts a timer to clean up old processed errors
   */
  startErrorCleanupTimer(): void {
    console.log('Starting error cleanup timer');

    try {
      if (this.#errorCleanupTimer) {
        clearInterval(this.#errorCleanupTimer);
      }

      const CLEANUP_INTERVAL = 30 * 60 * 1000; // 30 minutes

      this.#errorCleanupTimer = setInterval(() => {
        console.log('Cleaning up processed terminal errors');
      }, CLEANUP_INTERVAL);
    } catch (error) {
      console.error('Error starting error cleanup timer:', error);
    }
  }

  async newDualiteShellProcess(webcontainer: WebContainer, terminal: ITerminal) {
    console.log('Creating new Dualite shell process');

    const args: string[] = [];

    const process = await webcontainer.spawn('/bin/jsh', ['--osc', ...args], {
      terminal: {
        cols: terminal.cols ?? 80,
        rows: terminal.rows ?? 15,
      },
    });
    console.log('JSH process spawned');

    const input = process.input.getWriter();
    this.#shellInputStream = input;

    const [internalOutput, terminalOutput] = process.output.tee();

    const jshReady = withResolvers<void>();

    let isInteractive = false;
    terminalOutput.pipeTo(
      new WritableStream({
        write: (data) => {
          console.log('Terminal output received:', data, isInteractive);

          if (!isInteractive) {
            /*
             * OSC stands for "Operating System Command" - it's a type of terminal control sequence.
             * OSC sequences are special codes that terminals use to communicate with applications.
             */
            const [, osc] = data.match(/\x1b\]654;([^\x07]+)\x07/) || [];

            /*
             * They follow this format:
             * Apply to shell.ts
             * Where:
             * ESC (\x1b) = Escape character (starts the sequence)
             * ] = Indicates this is an OSC sequence
             * <number> = The OSC command number
             * ; = Separator
             * <data> = The actual data/message
             * BEL (\x07) = Bell character (ends the sequence)
             */

            if (osc === 'interactive') {
              // wait until we see the interactive OSC
              isInteractive = true;
              console.log('EXECUTION DONE');
              jshReady.resolve();
            }
          } else {
            // Check for prompt OSC code to reset running state for manual commands
            const [, osc] = data.match(/\x1b\]654;([^\x07]+)\x07/) || [];

            /*
             * Think of OSC as the shell's way of saying:
             * 📟 "Hey, I'm ready!" (interactive)
             * ✅ "Command finished!" (prompt)
             * 🚪 "Command exited with code X" (exit)
             */

            if (osc === 'prompt' && !this.isRunning.get().executingCommand?.includes('#ai')) {
              // this was the problem, we were setting isRunning to false when the ai prompt was received,
              console.log('Manual command completed, setting isRunning to false', this.isRunning.get());
              this.isRunning.set({ executing: false, executingCommand: undefined });
            }
          }

          // Add output to error buffer for real-time detection
          this.#errorBuffer.append(data);

          // Process stream directly for error detection
          this.processStreamForErrors(data);

          terminal.write(data);
        },
      }),
    );

    terminal.onData((data) => {
      console.log('Terminal input received (DualiteShell):', data);

      if (isInteractive) {
        console.log('Writing to shell input:', data);

        /*
         * Handle special characters:
         * - Backspace/Delete: remove last character from buffer
         * - Ctrl+C: clear the current input buffer
         * - Enter: process command submission
         */
        if (data === '\u0008' || data === '\u007f') {
          this.#currentInputBuffer = this.#currentInputBuffer.slice(0, -1);
        } else if (data === '\u0003') {
          this.#currentInputBuffer = '';
        } else if ((data.length === 1 && data.includes('\r')) || data.includes('\n')) {
          /*
           * Command submission detected
           * Extract the command (everything before the newline)
           */
          let command = this.#currentInputBuffer
            .replace(/\r?\n$/, '') // Remove trailing newline
            .replace(/\r$/, '') // Remove trailing carriage return
            .trim();

          // Remove any remaining control characters for cleaner command extraction
          command = command.replace(/[\u0000-\u001F\u007F]/g, '').trim();

          console.log('Manual command submitted:', command); // this is the command that is being executed manually
          console.log('Setting isRunning to true');

          // Store the command context for error handling
          if (command && command.trim() !== '') {
            this.#lastCommandContext = command;
            this.isRunning.set({ executing: true, executingCommand: command });
          }

          // Clear the buffer for the next command
          this.#currentInputBuffer = '';
        } else {
          // Regular character input - add to buffer
          this.#currentInputBuffer += data;
        }

        input.write(data);
      }
    });

    await jshReady.promise;
    console.log('Dualite shell process ready');

    return { process, output: internalOutput };
  }

  get terminalStatus() {
    console.log('The current running process: ', this.executionState.get());

    if (!this.isInitialized.get()) {
      return TerminalStatus.DISCONNECTED;
    }

    if (this.isRunning.get().executing) {
      return TerminalStatus.EXECUTING;
    }

    return TerminalStatus.FREE;
  }

  /**
   * Process each chunk of the terminal output stream directly for errors
   * This replaces the interval-based scanning with direct stream processing
   */
  processStreamForErrors(data: string): void {
    // Skip empty data
    if (!data.trim()) {
      return;
    }

    // First check for shell prompt to reset state
    if (this.#errorEmitter.processShellPrompt(data)) {
      console.log('Shell prompt detected in stream, resetting error state');
      return;
    }

    // Check for real-time errors in this chunk using ErrorPatterns
    if (ErrorPatterns.hasRealTimeError(data)) {
      console.log('Real-time error detected in stream chunk');

      // Extract error content from buffer
      const errorContent = this.#errorBuffer.extractErrorLines();

      console.log('Error content:', errorContent);

      // Clean the error content
      const cleanedContent = this.#errorBuffer.cleanTerminalForErrors(errorContent);
      const errorMessage = this.#errorBuffer.extractErrorMessage(errorContent);

      console.log('Cleaned error content:', cleanedContent);
      console.log('Error message:', errorMessage);

      // Skip benign errors
      if (ErrorPatterns.isBenignError(cleanedContent)) {
        console.log('Benign error detected, ignoring');
        return;
      }

      // Emit the error with cleaned output
      this.#errorEmitter.emitTerminalError(cleanedContent, 1, this.#lastCommandContext);

      // Trim buffer to avoid duplicates but keep context
      this.#errorBuffer.trim(500);
    }
  }

  async getPipedCommandExecutionResult(): Promise<ExecutionResult> {
    console.log('Getting piped command execution result');
    return this.waitTillOscCode('prompt');
  }

  async getCurrentExecutionResult(): Promise<ExecutionResult> {
    console.log('Getting current execution result');
    return this.waitTillOscCode('exit');
  }

  async waitTillOscCode(waitCode: string) {
    console.log('Waiting for OSC code:', waitCode);

    let fullOutput = '';
    let exitCode: number = 0;

    if (!this.#outputStream) {
      console.log('No output stream available');
      return { output: fullOutput, exitCode };
    }

    const tappedStream = this.#outputStream;

    while (true) {
      const { value, done } = await tappedStream.read();

      if (done) {
        console.log('Stream ended');
        break;
      }

      const text = value || '';
      fullOutput += text;

      // Add to real-time error detection buffer
      this.#errorBuffer.append(text);

      // Check if command completion signal with exit code
      const [, osc, , , code] = text.match(/\x1b\]654;([^\x07=]+)=?((-?\d+):(\d+))?\x07/) || [];

      if (osc === 'exit') {
        exitCode = parseInt(code, 10);
        console.log('Received exit code:', exitCode);
      }

      if (osc === waitCode) {
        console.log('Received waited OSC code:', waitCode);
        break;
      }
    }

    return { output: fullOutput, exitCode };
  }
}

/**
 * Cleans and formats terminal output while preserving structure and paths
 * Handles ANSI, OSC, and various terminal control sequences
 */
export function cleanTerminalOutput(input: string): string {
  console.log('Cleaning terminal output');

  // Step 1: Remove OSC sequences
  const removeOsc = input
    .replace(/\x1b\](\d+;[^\x07\x1b]*|\d+[^\x07\x1b]*)\x07/g, '')
    .replace(/\](\d+;[^\n]*|\d+[^\n]*)/g, '');

  // Step 2: Remove ANSI escape sequences and color codes more thoroughly
  const removeAnsi = removeOsc
    // Remove all escape sequences with parameters
    .replace(/\u001b\[[\?]?[0-9;]*[a-zA-Z]/g, '')
    .replace(/\x1b\[[\?]?[0-9;]*[a-zA-Z]/g, '')
    // Remove color codes
    .replace(/\u001b\[[0-9;]*m/g, '')
    .replace(/\x1b\[[0-9;]*m/g, '')
    // Clean up any remaining escape characters
    .replace(/\u001b/g, '')
    .replace(/\x1b/g, '');

  // Step 3: Remove spinner characters and progress indicators
  const removeSpinners = removeAnsi
    // Remove spinner characters (⠁, ⠂, ⠄, etc.) - combining both patterns
    .replace(/[⠁⠂⠄⡀⢀⠠⠐⠈⢎⣀⣄⣤⣦⣧⣶⣿]+/g, '')
    // Remove emoji indicators from yarn/npm
    .replace(/🔍|🚚|🔗|🔨|[✨]/g, '')
    // Remove progress bars
    .replace(/\[[\s#-]*\]/g, '')
    // Remove progress indicators like [1/4], [2/4], etc.
    .replace(/\[\d+\/\d+\]/g, '')
    // Remove percentage indicators
    .replace(/\[\d+%\]/g, '')
    // Remove yarn/npm progress indicators
    .replace(/\[\-+\]\s*\d*\/\d*/g, '')
    .replace(/\[#+\-*\]\s*\d*\/\d*/g, '');

  // Step 4: Clean up numeric sequences (package counts, etc)
  const cleanNumbers = removeSpinners
    // Remove consecutive numeric packages (0/322, 1/322, etc)
    .replace(/\b\d+\/\d+\b\s+(?:\d+\/\d+\s+)+/g, '')
    // Remove lone numbers that are likely package counts
    .replace(/\b\d+\/\d+\b(?!\S)/g, '');

  // Step 5: Process lines to remove common package duplication patterns
  const lines = cleanNumbers.split('\n');
  const uniquePackageLines: string[] = [];
  const seenPackages = new Set<string>();

  for (const line of lines) {
    // Skip empty lines
    if (!line.trim()) {
      continue;
    }

    // Check if line contains only package references
    if (/^([@\w\-\.]+\/)?[\w\-\.]+@[\^~]?\d+\.\d+\.\d+(?:\-[a-zA-Z]+\.\d+)?(\s+|$)/.test(line)) {
      // Extract all package references from the line
      const packages =
        line.match(/(?:[@\w\-\.]+\/)?[\w\-\.]+@[\^~]?\d+\.\d+\.\d+(?: \|\| [\^~]?\d+\.\d+\.\d+)?/g) || [];

      // Only keep the first instance of each package
      const uniquePackages = packages.filter((pkg) => {
        const baseName = pkg.split('@')[0];

        if (seenPackages.has(baseName)) {
          return false;
        }

        seenPackages.add(baseName);
        return true;
      });

      // If we have unique packages, add them as a single line
      if (uniquePackages.length > 0) {
        uniquePackageLines.push(uniquePackages.join(' '));
      }
    } else {
      // This isn't a package line, keep it
      uniquePackageLines.push(line);
    }
  }

  // Step 6: Clean up package resolution lines further
  const cleanPackages = uniquePackageLines
    .join('\n')
    // Remove repetitive package checks and versions - safely using a callback function
    .replace(/(?:[@\w\-\.]+\/)?[\w\-\.]+@[\^~]?\d+\.\d+\.\d+(?:\s+\S+)+/g, (match) => {
      return match.split(/\s+/)[0];
    })
    // Clean up dependency lists (remove duplicates in sequence)
    .replace(/([@\w-]+(?:@[\d.]+)?(?:\s*->.*)?)\n\1+/g, '$1')
    // Clean up network retry messages
    .replace(/(info There appears to be trouble with your network connection\. Retrying\.\.\.)\s*\n+/g, '$1\n');

  // Step 7: Clean up carriage returns and newlines
  const cleanNewlines = cleanPackages
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n{3,}/g, '\n\n');

  // Step 8: Add newlines at key breakpoints while preserving paths
  const formatOutput = cleanNewlines
    // Preserve prompt line
    .replace(/^([~\/][^\n❯]+)❯/m, '$1\n❯')
    // Add newline before command output indicators
    .replace(/(?<!^|\n)>/g, '\n>')
    // Add newline before error keywords without breaking paths
    .replace(/(?<!^|\n|\w)(error|failed|warning|Error|Failed|Warning):/g, '\n$1:')
    // Add newline before 'at' in stack traces without breaking paths
    .replace(/(?<!^|\n|\/)(at\s+(?!async|sync))/g, '\nat ')
    // Ensure 'at async' stays on same line
    .replace(/\bat\s+async/g, 'at async')
    // Add newline before npm/yarn indicators
    .replace(/(?<!^|\n)(npm ERR!|yarn add v|info|success)/g, '\n$1');

  // Step 9: Group similar lines (info dependencies)
  const lines2 = formatOutput.split('\n');
  let i = 0;

  while (i < lines2.length) {
    // Look for "info Direct dependencies" and "info All dependencies" sections
    if (/^info\s+(Direct|All)\s+dependencies/.test(lines2[i])) {
      const header = lines2[i];
      let j = i + 1;

      // Collect package lines that start with ├─ or └─
      const packageLines: string[] = [];

      while (j < lines2.length && /^[├└]─\s/.test(lines2[j])) {
        packageLines.push(lines2[j]);
        j++;
      }

      // Replace with a summary
      if (packageLines.length > 0) {
        lines2.splice(i, j - i, `${header} (${packageLines.length} packages)`);
      }
    }

    i++;
  }

  // Step 10: Clean up whitespace while preserving intentional spacing
  const filteredLines = lines2.filter((line) => line.trim()).map((line) => line.trim());

  // Remove consecutive duplicate lines
  const uniqueLines = filteredLines.filter((line, index, arr) => {
    // Special handling for numbers-only lines (like package counts)
    if (/^\d+\/\d+$/.test(line)) {
      return false; // Remove all number-only lines
    }

    return line !== arr[index - 1];
  });

  const cleanSpaces = uniqueLines.join('\n');

  // Step 11: Final cleanup
  return cleanSpaces
    .replace(/\n{3,}/g, '\n\n') // Replace multiple newlines with double newlines
    .replace(/:\s+/g, ': ') // Normalize spacing after colons
    .replace(/\s{2,}/g, ' ') // Remove multiple spaces
    .replace(/^\s+|\s+$/g, '') // Trim start and end
    .replace(/\u0000/g, ''); // Remove null characters
}

export function newDualiteShellProcess() {
  console.log('Creating new Dualite shell instance');
  return new DualiteShell();
}
